import { useState, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import useReveal from "../lib/useReveal";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/zosw6dgo_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2012_58_49%20PM.png";
const ADITI_IMG =
  "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/asz9zmg3_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2012_44_30%20PM.png";
const JACKSON_IMG =
  "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/xau26hmh_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2012_09_53%20PM.png";
const SHIKHA_IMG =
  "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/7u9pt15y_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2012_05_16%20PM.png";

const team = [
  {
    id: "satyam",
    name: "Satyam Yadav",
    role: "Founder",
    initials: "SY",
    accent: "#C25E3E",
    eyebrow: "The Founder's Note",
    image: FOUNDER_IMG,
    quote:
      "I built Aasa for the people I couldn't help with one specialist alone.",
    paras: [
      "After years inside fragmented healthcare — watching patients bounce between a doctor, a dietitian and a trainer who never spoke to each other — I started Aasa Healthscience & Nutrition as the opposite of that.",
      "One team. One plan. One human at the centre. Every protocol we design is rooted in measurable medicine, real food, and movement that respects the body in front of us — not a textbook version of it.",
    ],
    credentials: [
      "Founder, Aasa Healthscience & Nutrition",
      "Metabolic Health Coach",
      "Holistic Wellness Strategist",
    ],
  },
  {
    id: "jackson",
    name: "Dr. Jackson John",
    role: "Emergency Medicine Physician",
    initials: "JJ",
    accent: "#1E3A2B",
    eyebrow: "Clinical Lead",
    image: JACKSON_IMG,
    quote: "Reverse the disease, don't just manage it.",
    paras: [
      "With 8+ years inside emergency medicine, Dr. Jackson has seen first-hand how diet, sleep and stress quietly become tomorrow's hospital admissions.",
      "At Aasa, he leads the medical screening, lab interpretation and risk-stratification for every client — making sure every plan is anchored in measurable clinical evidence.",
    ],
    credentials: [
      "MBBS · Emergency Medicine",
      "8+ Years of Clinical Practice",
      "Metabolic & Lifestyle Medicine",
    ],
  },
  {
    id: "aditi",
    name: "Dr. Aditi Kushwah",
    role: "OB-GYN Resident Doctor",
    initials: "AK",
    accent: "#839788",
    eyebrow: "Women's Health Lead",
    image: ADITI_IMG,
    quote: "Hormones are a signal, not a sentence.",
    paras: [
      "Dr. Aditi specialises in women's reproductive and hormonal health — from PCOD and PCOS to thyroid, fertility and post-partum recovery.",
      "She translates complex endocrine science into food, movement and lifestyle shifts that women can actually live with, so cycles regulate without their lives being interrupted.",
    ],
    credentials: [
      "MBBS · OB-GYN Resident",
      "PCOS & PCOD Specialist",
      "Women's Hormonal Health",
    ],
  },
  {
    id: "shikha",
    name: "Shikha Maheshwari",
    role: "Yoga Trainer · Sport Nutritionist · Fitness Coach",
    initials: "SM",
    accent: "#C25E3E",
    eyebrow: "Movement & Nutrition Lead",
    image: SHIKHA_IMG,
    quote: "Strength is built in the kitchen, refined on the mat.",
    paras: [
      "A certified yoga trainer, certified sport nutritionist and certified fitness trainer rolled into one — Shikha brings 5+ years of practice and has personally coached hundreds of clients to lasting results.",
      "Her programs bridge ancient breath work with modern sports nutrition, designed for real Indian kitchens, real busy lives, and real measurable progress.",
    ],
    credentials: [
      "Certified Yoga Trainer",
      "Certified Sport Nutritionist",
      "5+ Years · 100s Coached",
    ],
  },
  {
    id: "physio",
    name: "Bharti Verma",
    role: "Neuro Physiotherapist · MPT (Neuro)",
    initials: "BV",
    accent: "#1E3A2B",
    eyebrow: "Recovery & Movement Lead",
    image:
      "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/ic60avlu_WhatsApp%20Image%202026-06-22%20at%2012.17.44%20PM.jpeg",
    quote:
      "When we move well, we function well — and when we function well, we live well.",
    paras: [
      "Movement is medicine. Every movement stimulates the nervous system, enhances circulation, and keeps muscles and joints healthy. Good posture ensures efficient alignment of the body, reducing stress on tissues and allowing optimal breathing, balance and coordination.",
      "Poor posture and inactivity, on the other hand, lead to muscle imbalances, restricted mobility, altered reflexes and impaired physiological function. As a Neuro Physiotherapist, I believe restoring movement and correcting posture are essential for improving reflexes, functional independence and overall quality of life.",
    ],
    credentials: [
      "Pursuing MPT (Neuro)",
      "Neuro Rehabilitation",
      "Movement Restoration",
    ],
  },
  {
    id: "ashish",
    name: "Ashish Verma",
    role: "Strength Coach",
    initials: "AV",
    accent: "#839788",
    eyebrow: "Strength & Conditioning",
    image:
      "https://customer-assets.emergentagent.com/job_vitality-plan/artifacts/4nsf0j1n_WhatsApp%20Image%202026-06-22%20at%2012.01.50%20PM.jpeg",
    quote: "Every repetition is a movement pattern the body remembers.",
    paras: [
      "As a strength coach with hundreds of individuals trained, one lesson stands out — people consistently underestimate the importance of exercise form and body mechanics. Poor technique, repeated over months and years, quietly turns into postural imbalances, joint stress, pain and lost performance. Proper movement, on the other hand, compounds into posture, strength and resilience that lasts for life.",
      "Corrective exercise, mobility work and addressing muscle imbalances aren't optional — they are essential. And because mobility, injury history, posture and goals differ from person to person, a one-size-fits-all program rarely delivers optimal results. The best training plans are carefully curated to the individual body in front of you.",
    ],
    credentials: [
      "Certified Strength Coach",
      "100s of Clients Trained",
      "Corrective Exercise & Mobility",
    ],
  },
];

export const Team = () => {
  const ref = useReveal();
  const [emblaRef, embla] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false,
  });
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setIndex(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => {
      embla.off("select", onSelect);
    };
  }, [embla]);

  // Auto-advance every 2s, pause on hover/focus
  useEffect(() => {
    if (!embla || paused) return;
    intervalRef.current = setInterval(() => {
      embla.scrollNext();
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [embla, paused]);

  const scrollTo = (i) => embla?.scrollTo(i);

  return (
    <section
      id="founder"
      data-testid="team-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      className="py-16 sm:py-24 lg:py-36 bg-[#FCFBF7] border-y border-[#D5CEBA] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12" ref={ref}>
        <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-5 sm:gap-6 mb-10 lg:mb-16">
          <div>
            <span className="uppercase text-[10px] sm:text-xs tracking-[0.28em] text-[#C25E3E] font-semibold">
              The Team
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 sm:mt-6 leading-[1.05] text-[#1E3A2B]">
              The minds behind
              <br />
              <span className="italic">every Aasa plan.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3" data-testid="team-controls">
            <CarouselButton
              direction="prev"
              testid="team-prev"
              onClick={() => embla?.scrollPrev()}
            />
            <span
              data-testid="team-counter"
              className="text-sm font-display text-[#1E3A2B]/70 min-w-[3.5rem] text-center"
            >
              {String(index + 1).padStart(2, "0")} / {String(team.length).padStart(2, "0")}
            </span>
            <CarouselButton
              direction="next"
              testid="team-next"
              onClick={() => embla?.scrollNext()}
            />
          </div>
        </div>

        <div className="reveal overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {team.map((m) => (
              <div
                key={m.id}
                data-testid={`team-slide-${m.id}`}
                className="flex-[0_0_100%] min-w-0 pr-0"
              >
                <TeamSlide member={m} />
              </div>
            ))}
          </div>
        </div>

        <div
          className="reveal mt-12 flex justify-center items-center gap-3"
          data-testid="team-dots"
        >
          {team.map((m, i) => (
            <button
              key={m.id}
              data-testid={`team-dot-${i}`}
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`rounded-full transition-all duration-500 ${
                i === index
                  ? "w-10 h-2.5 bg-[#C25E3E]"
                  : "w-2.5 h-2.5 bg-[#1E3A2B]/25 hover:bg-[#1E3A2B]/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const TeamSlide = ({ member }) => (
  <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-center">
    <div className="lg:col-span-5">
      <div className="relative">
        <div className="rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden border border-[#D5CEBA] aspect-[4/5] lg:aspect-auto lg:h-[34rem]">
          {member.image ? (
            <img
              src={member.image}
              alt={`${member.name} — ${member.role}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <PhotoPlaceholder
              initials={member.initials}
              accent={member.accent}
              role={member.role}
            />
          )}
        </div>
        <div className="absolute -bottom-5 -right-2 lg:-right-6 bg-[#1E3A2B] text-[#FCFBF7] rounded-2xl px-4 py-3 sm:px-5 sm:py-4 max-w-[14rem] sm:max-w-[18rem]">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-70">
            {member.role.split("·")[0].trim()}
          </div>
          <div className="font-display text-xl sm:text-2xl mt-1 leading-tight">
            {member.name}
          </div>
        </div>
      </div>
    </div>

    <div className="lg:col-span-7 lg:pl-8 mt-6 lg:mt-0">
      <span className="uppercase text-[10px] sm:text-xs tracking-[0.28em] text-[#C25E3E] font-semibold">
        {member.eyebrow}
      </span>
      <div className="mt-4 sm:mt-6 flex items-start gap-3">
        <Quote className="w-6 h-6 sm:w-8 sm:h-8 text-[#C25E3E] mt-1.5 sm:mt-2 flex-shrink-0" />
        <h3 className="font-display italic text-2xl sm:text-3xl lg:text-5xl tracking-tight leading-[1.1] text-[#1E3A2B]">
          &ldquo;{member.quote}&rdquo;
        </h3>
      </div>

      <div className="mt-6 sm:mt-8 space-y-4 sm:space-y-5 text-base sm:text-lg text-[#1E3A2B]/80 leading-relaxed max-w-2xl">
        {member.paras.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
        <p className="font-display italic text-xl sm:text-2xl text-[#1E3A2B] pt-1 sm:pt-2">
          — {member.name}
        </p>
      </div>

      <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        {member.credentials.map((c) => (
          <span
            key={c}
            className="inline-flex justify-center text-center text-[10px] sm:text-xs uppercase tracking-[0.18em] rounded-full border border-[#1E3A2B]/25 px-3 sm:px-4 py-2 sm:py-3 text-[#1E3A2B]/80"
          >
            {c}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const PhotoPlaceholder = ({ initials, accent, role }) => (
  <div
    className="w-full h-full relative flex flex-col items-center justify-center grain"
    style={{
      background: `linear-gradient(150deg, ${accent} 0%, #1E3A2B 100%)`,
    }}
  >
    <div
      className="font-display text-[10rem] lg:text-[14rem] text-[#FCFBF7]/95 leading-none"
      aria-hidden
    >
      {initials}
    </div>
    <div className="absolute bottom-6 left-6 right-6 text-center">
      <span className="inline-block bg-[#FCFBF7]/15 backdrop-blur-sm text-[#FCFBF7] text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border border-[#FCFBF7]/20">
        Portrait coming soon
      </span>
    </div>
    <div className="absolute top-6 left-6 text-[#FCFBF7]/70 text-xs uppercase tracking-[0.22em] max-w-[12rem] text-left">
      {role}
    </div>
  </div>
);

const CarouselButton = ({ direction, onClick, testid }) => {
  const Icon = direction === "prev" ? ArrowLeft : ArrowRight;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={direction === "prev" ? "Previous team member" : "Next team member"}
      className="inline-flex w-11 h-11 items-center justify-center rounded-full border border-[#1E3A2B]/30 text-[#1E3A2B] hover:bg-[#1E3A2B] hover:text-[#FCFBF7] transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

export default Team;
